import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { useAuth } from "@/context/AuthContext";
import { useCustomerLevels, CUSTOMER_LEVELS_FALLBACK } from "@/hooks/useCustomerLevels";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GiftVoucherTender } from "@/components/giftvouchers/GiftVoucherTender";
import { Loader2, AlertTriangle, Star, Mail, WifiOff, Banknote, CreditCard, Building, Split, Gift, Lock, X } from "lucide-react";
import { SplitInvoiceWrapper } from "./SplitInvoiceWrapper";
import { queueOfflineSale, getPendingOfflineSalesCount, getCachedCustomers } from "@/utils/offlineStorage";
import { isOnline as connectivityIsOnline, subscribeOnline } from "@/utils/connectivity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Customer level discount rates come from `/api/customer-levels` via
// the shared `useCustomerLevels` hook. This constant is only a fallback
// used if the hook hasn't resolved yet or the API is unreachable.
const CUSTOMER_LEVEL_DISCOUNTS_FALLBACK = CUSTOMER_LEVELS_FALLBACK;

// Split Payment Modal Component
const SplitPaymentModal = ({ open, onClose, total, onConfirm, hasCustomer, availableCredit, creditLimit, loyaltyCredit = 0 }) => {
  const [cashAmount, setCashAmount] = useState("");
  const [cardAmount, setCardAmount] = useState("");
  const [accountAmount, setAccountAmount] = useState("");
  const [loyaltyAmount, setLoyaltyAmount] = useState("");
  const [activeField, setActiveField] = useState("cash");
  
  // Check if account amount exceeds available credit
  const accountExceedsCredit = () => {
    if (!hasCustomer) return false;
    const account = parseFloat(accountAmount) || 0;
    if (account <= 0) return false;
    if (creditLimit <= 0) return true;
    return account > availableCredit;
  };
  
  // Check if loyalty amount exceeds available balance
  const loyaltyExceedsBalance = () => {
    const loyalty = parseFloat(loyaltyAmount) || 0;
    return loyalty > loyaltyCredit;
  };

  // Handle close with reset
  const handleClose = () => {
    onClose();
    // Reset state for next open
    setTimeout(() => {
      setCashAmount("");
      setCardAmount("");
      setAccountAmount("");
      setLoyaltyAmount("");
      setActiveField("cash");
    }, 200);
  };

  const getTotalEntered = () => {
    return (parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0) + (parseFloat(accountAmount) || 0) + (parseFloat(loyaltyAmount) || 0);
  };

  const getRemaining = () => {
    return Math.max(0, total - getTotalEntered());
  };

  const handleKeypadPress = (value) => {
    const updateAmount = (current) => {
      if (value === "C") return "";
      if (value === "." && current.includes(".")) return current;
      if (value === "BS") return current.slice(0, -1);
      return current + value;
    };

    switch (activeField) {
      case "cash":
        setCashAmount(updateAmount(cashAmount));
        break;
      case "card":
        setCardAmount(updateAmount(cardAmount));
        break;
      case "account":
        setAccountAmount(updateAmount(accountAmount));
        break;
      case "loyalty":
        setLoyaltyAmount(updateAmount(loyaltyAmount));
        break;
    }
  };

  const handleFillRemaining = () => {
    const remaining = getRemaining();
    if (remaining > 0) {
      switch (activeField) {
        case "cash":
          setCashAmount(remaining.toFixed(2));
          break;
        case "card":
          setCardAmount(remaining.toFixed(2));
          break;
        case "account":
          setAccountAmount(remaining.toFixed(2));
          break;
        case "loyalty":
          // For loyalty, cap at available balance
          setLoyaltyAmount(Math.min(remaining, loyaltyCredit).toFixed(2));
          break;
      }
    }
  };

  const handleConfirm = () => {
    const payments = [];
    const cash = parseFloat(cashAmount) || 0;
    const card = parseFloat(cardAmount) || 0;
    const account = parseFloat(accountAmount) || 0;
    const loyalty = parseFloat(loyaltyAmount) || 0;

    if (cash > 0) payments.push({ method: "CASH", amount: cash });
    if (card > 0) payments.push({ method: "CARD", amount: card });
    if (account > 0) payments.push({ method: "ACCOUNT", amount: account });
    if (loyalty > 0) payments.push({ method: "LOYALTY_CREDIT", amount: loyalty });

    if (payments.length === 0) {
      toast.error("Please enter at least one payment amount");
      return;
    }

    const totalPaid = cash + card + account + loyalty;
    if (totalPaid < total) {
      toast.error(`Payment is short by R ${(total - totalPaid).toFixed(2)}`);
      return;
    }
    
    // Check credit limit for account portion
    if (account > 0) {
      if (creditLimit <= 0) {
        toast.error("This customer has no credit facility (credit limit R 0.00). On-account amount is not allowed.");
        return;
      }
      if (account > availableCredit) {
        toast.error(`Account amount (R ${account.toFixed(2)}) exceeds available credit (R ${availableCredit.toFixed(2)})`);
        return;
      }
    }
    
    // Check loyalty credit balance
    if (loyalty > loyaltyCredit) {
      toast.error(`Loyalty credit (R ${loyalty.toFixed(2)}) exceeds available balance (R ${loyaltyCredit.toFixed(2)})`);
      return;
    }

    onConfirm(payments, cash > total ? cash - total + card + account + loyalty - total : 0);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Split className="h-6 w-6 text-purple-600" />
            Split Payment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Total to Pay */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <div className="text-sm text-purple-600 font-medium">Total to Pay</div>
            <div className="text-3xl font-bold text-purple-700">R {total.toFixed(2)}</div>
          </div>

          {/* Payment Fields */}
          <div className="space-y-3">
            {/* Cash */}
            <div 
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                activeField === "cash" ? "bg-green-100 border-2 border-green-500 ring-2 ring-green-200" : "bg-gray-50 border border-gray-200"
              }`}
              onClick={() => setActiveField("cash")}
            >
              <Banknote className={`h-6 w-6 ${activeField === "cash" ? "text-green-600" : "text-gray-400"}`} />
              <span className="font-semibold flex-1">CASH</span>
              <Input
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                onFocus={() => setActiveField("cash")}
                className="w-32 text-right font-bold text-lg"
                placeholder="0.00"
              />
            </div>

            {/* Card */}
            <div 
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                activeField === "card" ? "bg-blue-100 border-2 border-blue-500 ring-2 ring-blue-200" : "bg-gray-50 border border-gray-200"
              }`}
              onClick={() => setActiveField("card")}
            >
              <CreditCard className={`h-6 w-6 ${activeField === "card" ? "text-blue-600" : "text-gray-400"}`} />
              <span className="font-semibold flex-1">CARD</span>
              <Input
                value={cardAmount}
                onChange={(e) => setCardAmount(e.target.value)}
                onFocus={() => setActiveField("card")}
                className="w-32 text-right font-bold text-lg"
                placeholder="0.00"
              />
            </div>

            {/* On Account - Always visible, disabled if no customer */}
            <div>
              <div 
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                  !hasCustomer
                    ? "bg-gray-100 border border-gray-200 opacity-60"
                    : accountExceedsCredit() 
                      ? "bg-red-100 border-2 border-red-500 ring-2 ring-red-200"
                      : activeField === "account" 
                        ? "bg-orange-100 border-2 border-orange-500 ring-2 ring-orange-200" 
                        : "bg-gray-50 border border-gray-200"
                }`}
                onClick={() => hasCustomer && setActiveField("account")}
              >
                <Building className={`h-6 w-6 ${!hasCustomer ? "text-gray-400" : accountExceedsCredit() ? "text-red-600" : activeField === "account" ? "text-orange-600" : "text-gray-400"}`} />
                <span className="font-semibold flex-1">ON ACCOUNT</span>
                <Input
                  value={accountAmount}
                  onChange={(e) => hasCustomer && setAccountAmount(e.target.value)}
                  onFocus={() => hasCustomer && setActiveField("account")}
                  className={`w-32 text-right font-bold text-lg ${accountExceedsCredit() ? "border-red-400" : ""}`}
                  placeholder="0.00"
                  disabled={!hasCustomer}
                />
              </div>
              <div className={`text-xs mt-1 px-1 ${!hasCustomer ? "text-gray-500" : accountExceedsCredit() ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                {!hasCustomer ? (
                  <>Select a customer in checkout to enable on-account payment</>
                ) : creditLimit > 0 ? (
                  <>Available credit: R {availableCredit.toFixed(2)}{accountExceedsCredit() && " - EXCEEDED!"}</>
                ) : (
                  <>No credit facility configured (limit R 0.00)</>
                )}
              </div>
            </div>
            
            {/* Loyalty Credit - Only if customer has loyalty balance */}
            {hasCustomer && loyaltyCredit > 0 && (
              <div>
                <div 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    loyaltyExceedsBalance() 
                      ? "bg-red-100 border-2 border-red-500 ring-2 ring-red-200"
                      : activeField === "loyalty" 
                        ? "bg-emerald-100 border-2 border-emerald-500 ring-2 ring-emerald-200" 
                        : "bg-gray-50 border border-gray-200"
                  }`}
                  onClick={() => setActiveField("loyalty")}
                >
                  <Gift className={`h-6 w-6 ${loyaltyExceedsBalance() ? "text-red-600" : activeField === "loyalty" ? "text-emerald-600" : "text-gray-400"}`} />
                  <span className="font-semibold flex-1">LOYALTY CREDIT</span>
                  <Input
                    value={loyaltyAmount}
                    onChange={(e) => setLoyaltyAmount(e.target.value)}
                    onFocus={() => setActiveField("loyalty")}
                    className={`w-32 text-right font-bold text-lg ${loyaltyExceedsBalance() ? "border-red-400" : ""}`}
                    placeholder="0.00"
                  />
                </div>
                <div className={`text-xs mt-1 px-1 ${loyaltyExceedsBalance() ? "text-red-600 font-semibold" : "text-emerald-600"}`}>
                  Available loyalty credit: R {loyaltyCredit.toFixed(2)}{loyaltyExceedsBalance() && " - EXCEEDED!"}
                </div>
              </div>
            )}
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-4 gap-2">
            {["7", "8", "9", "C"].map((key) => (
              <Button
                key={key}
                variant="outline"
                className={`h-12 text-xl font-bold ${key === "C" ? "bg-red-100 text-red-600 hover:bg-red-200" : ""}`}
                onClick={() => handleKeypadPress(key)}
              >
                {key}
              </Button>
            ))}
            {["4", "5", "6", "BS"].map((key) => (
              <Button
                key={key}
                variant="outline"
                className={`h-12 text-xl font-bold ${key === "BS" ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" : ""}`}
                onClick={() => handleKeypadPress(key)}
              >
                {key === "BS" ? "←" : key}
              </Button>
            ))}
            {["1", "2", "3", "."].map((key) => (
              <Button
                key={key}
                variant="outline"
                className="h-12 text-xl font-bold"
                onClick={() => handleKeypadPress(key)}
              >
                {key}
              </Button>
            ))}
            <Button
              variant="outline"
              className="h-12 text-xl font-bold col-span-2"
              onClick={() => handleKeypadPress("0")}
            >
              0
            </Button>
            <Button
              variant="outline"
              className="h-12 text-sm font-bold col-span-2 bg-purple-100 text-purple-700 hover:bg-purple-200"
              onClick={handleFillRemaining}
            >
              Fill Rest
            </Button>
          </div>

          {/* Summary */}
          <div className="bg-gray-100 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Total Entered:</span>
              <span className={`font-bold ${getTotalEntered() >= total ? "text-green-600" : "text-gray-700"}`}>
                R {getTotalEntered().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Remaining:</span>
              <span className={`font-bold ${getRemaining() > 0 ? "text-red-600" : "text-green-600"}`}>
                R {getRemaining().toFixed(2)}
              </span>
            </div>
            {getTotalEntered() > total && (
              <div className="flex justify-between text-sm border-t pt-2">
                <span>Change (from cash):</span>
                <span className="font-bold text-green-600">
                  R {(getTotalEntered() - total).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={getTotalEntered() < total}
            className="bg-green-600 hover:bg-green-700"
          >
            Confirm Split Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CheckoutModal = ({ open, onClose, cart, calculateTotals, priceLevel, onComplete, vatEnabled = true, promotionDiscount = 0, appliedPromotions = [], promoPluCodes = new Set(), initialCustomer = null, salesperson = null, onVoucherIneligible = () => {} }) => {
  const { user } = useAuth();
  const { map: CUSTOMER_LEVEL_DISCOUNTS } = useCustomerLevels();
  // Prefer the logged-in user's real name for the "Cashier / Sales Person"
  // field on receipts. Falls back to a generic label only when no user is
  // in context (should never happen at the till).
  const cashierName = user?.name || user?.full_name || user?.username || "Cashier";
  const [transactionType, setTransactionType] = useState("CASH SALE");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("CASH");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [payments, setPayments] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [completedTransaction, setCompletedTransaction] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  
  // Split payment modal state
  const [showSplitPayment, setShowSplitPayment] = useState(false);
  
  // Order number state (for customers that require it)
  const [customerOrderNumber, setCustomerOrderNumber] = useState("");
  // Reason for refund — required whenever the cashier selects any REFUND
  // transaction type (CASH REFUND / CARD REFUND / ACCOUNT REFUND). Persisted
  // on the sale doc as `refund_reason` so every credit note carries an audit
  // trail.
  const [refundReason, setRefundReason] = useState("");
  // PIN-override state for the "customer requires order number" gate.
  // Set to the approver's name when a manager approves selling this cart
  // WITHOUT an order number. Auto-resets when the customer changes.
  const [orderNumberOverrideBy, setOrderNumberOverrideBy] = useState(null);
  const [orderNumberPinOpen, setOrderNumberPinOpen] = useState(false);
  const [orderNumberPin, setOrderNumberPin] = useState("");
  const [orderNumberPinBusy, setOrderNumberPinBusy] = useState(false);
  const [orderNumberPinError, setOrderNumberPinError] = useState("");
  
  // Sale note state
  const [saleNote, setSaleNote] = useState("");
  // Manager override to allow selling below cost (stock clearance). When
  // the backend blocks a sale with HTTP 400 "Sale blocked — ... BELOW COST",
  // we surface a checkbox to tick and re-submit. Reset every time the
  // modal closes/opens so a previous approval never carries over.
  const [allowBelowCost, setAllowBelowCost] = useState(false);
  const [belowCostBlockDetail, setBelowCostBlockDetail] = useState("");
  
  // Email prompt state
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailPromptAddress, setEmailPromptAddress] = useState("");
  const [pendingSaleForEmail, setPendingSaleForEmail] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Opt-in: cashier ticks to email the invoice. Default OFF — same UX as the
  // Account Receipt and Customer Journal flows. Resets on close.
  const [emailInvoiceToCustomer, setEmailInvoiceToCustomer] = useState(false);

  // Synchronous in-flight guard. `disabled={processing}` on the button
  // alone doesn't protect against rapid double-clicks in the same React
  // render tick — `setProcessing(true)` is batched, so a second click
  // within ~16ms fires handleCompleteSale again before `processing` has
  // flipped in the DOM. Refs update synchronously, so this guard blocks
  // the second entry even inside the same tick. This is the exact bug
  // the user hit: "the system stalls or hangs for a few seconds and
  // then the cashier clicks on accept again, resulting in duplicate
  // invoices".
  const inFlightRef = useRef(false);

  // Idempotency key that is STABLE across every /sales/transaction retry
  // within the same checkout attempt. A fresh key is minted at the start
  // of each processSale() invocation, so if the cashier hits Process
  // twice after an 8s axios timeout the SECOND click sends the SAME key
  // as the first — the backend's unique index on ``sales.idempotency_key``
  // then short-circuits the duplicate insert and returns the original
  // sale. Prevents the "sale posted twice" bug (SALE-018233 / SALE-018239).
  const idempotencyKeyRef = useRef(null);
  const mintIdempotencyKey = () => {
    const k = (typeof crypto !== "undefined" && crypto?.randomUUID?.())
      || `sale-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    idempotencyKeyRef.current = k;
    return k;
  };
  // Cashier can flick this off when the cart contains items that don't
  // qualify for the customer's loyalty/level discount (e.g. promo items,
  // airtime, special-order goods). Resets back to ON whenever the customer
  // is changed so the cashier doesn't accidentally carry it over.
  const [skipCustomerDiscount, setSkipCustomerDiscount] = useState(false);
  
  // Offline state — uses the connectivity probe (subscribeOnline) so we
  // correctly detect "WiFi connected but no internet" situations that
  // navigator.onLine alone misses.
  const [isOnline, setIsOnline] = useState(connectivityIsOnline());

  useEffect(() => {
    const unsubscribe = subscribeOnline((nowOnline) => setIsOnline(nowOnline));
    return unsubscribe;
  }, []);

  // Calculate customer discount percentage (level discount + additional discount)
  const getCustomerDiscountPercent = () => {
    if (!selectedCustomer) return 0;
    const levelDiscount = CUSTOMER_LEVEL_DISCOUNTS[selectedCustomer.customer_level] || 0;
    const additionalDiscount = selectedCustomer.discount_percent || 0;
    return levelDiscount + additionalDiscount;
  };

  const customerDiscountPercent = getCustomerDiscountPercent();

  // Calculate totals based on selected customer (for VAT exemption and discounts)
  const totals = calculateTotals(selectedCustomer);
  
  // Reset the "skip discount" toggle whenever the customer changes so the
  // cashier doesn't accidentally carry it forward to a different shopper.
  useEffect(() => {
    setSkipCustomerDiscount(false);
    // Any prior PIN override is customer-specific; drop it when the
    // customer changes so a new customer that needs a PO gets blocked
    // afresh.
    setOrderNumberOverrideBy(null);
  }, [selectedCustomer?.id]);

  // Effective discount percent honours the cashier's "cancel" toggle so the
  // sale payload, receipt and audit pack all line up.
  const effectiveCustomerDiscountPercent = skipCustomerDiscount ? 0 : customerDiscountPercent;

  // Resolve the effective per-item VAT rate at sale time based on the
  // active customer. "Exempt" / "VAT Exempt Eligible" categories are
  // customer-conditional: only stripped when the customer holds a VAT
  // exemption certificate. Persisted onto every sale item so downstream
  // reporting (profit/COGS, VAT201) doesn't have to guess.
  const resolveItemVatRate = (item) => {
    // Line was already priced at 0% VAT (either the product's dedicated
    // VAT-Exempt Selling Price was substituted, OR shelf/1.15 was applied
    // when the exempt customer was picked). Do not strip again.
    if (item.vat_exempt_price_applied || item.vat_stripped_at_till) return 0;
    const cat = item.vat_category || "Standard 15%";
    if (cat === "Zero-rated 0%" || cat === "0%" || cat === "Zero-rated") return 0;
    if (cat === "VAT Exempt Eligible" || cat === "Exempt") {
      return selectedCustomer?.has_vat_exemption ? 0 : 15;
    }
    return 15;
  };

  // Is this line's VAT being customer-conditionally stripped right now?
  // Only true when both product category AND customer flag agree, AND
  // the price hasn't already been adjusted at the till (Sales.jsx
  // addToCart / repriceCartForCustomer sets vat_stripped_at_till or
  // vat_exempt_price_applied when it substitutes the unit_price).
  const isConditionallyExempt = (item) => {
    if (item.vat_exempt_price_applied || item.vat_stripped_at_till) return false;
    const cat = item.vat_category || "Standard 15%";
    return (cat === "Exempt" || cat === "VAT Exempt Eligible")
      && !!selectedCustomer?.has_vat_exemption;
  };

  // Return a copy of the cart with `vat_rate` snapshotted onto each line.
  // For customer-conditional exempt lines we also rewrite `unit_price` and
  // `total` down to the excl-VAT figure — so the receipt/invoice shows the
  // exempt customer a clean 0% line at the pre-VAT price (per user request),
  // and downstream reports never over-count revenue for that sale.
  const cartWithResolvedVat = () => cart.map((it) => {
    const vat_rate = resolveItemVatRate(it);
    // Price was ALREADY adjusted at the till (VAT-Exempt Selling Price
    // substituted OR shelf/1.15 stripped when the exempt customer was
    // picked). unit_price is final — just tag the line as 0% VAT and
    // record the exemption for downstream reporting.
    if (it.vat_exempt_price_applied || it.vat_stripped_at_till) {
      return {
        ...it,
        vat_rate,
        vat_exemption_applied: true,
      };
    }
    if (isConditionallyExempt(it)) {
      // Prefer the product's dedicated VAT-Exempt Selling Price
      // (entered incl-VAT on the Pricing tab) when one is captured on
      // the cart line. This is the case when the cashier picks the
      // exempt customer AT CHECKOUT (Sales.jsx addToCart couldn't
      // apply the substitution because activeCustomer was null then).
      // Falls back to the shelf/1.15 auto-strip.
      const special = Number(it.vat_exempt_price_incl_vat) || 0;
      const qty = Number(it.quantity) || 1;
      const discountPct = Number(it.discount_percent) || 0;
      const discountFactor = 1 - discountPct / 100;

      let strippedUnit;
      let strippedTotal;
      if (special > 0) {
        strippedUnit = special / 1.15;
        strippedTotal = strippedUnit * qty * discountFactor;
      } else {
        strippedUnit = Number(it.unit_price) / 1.15;
        strippedTotal = Number(it.total) / 1.15;
      }
      return {
        ...it,
        vat_rate,
        // Preserve the original shelf price for audit — invisible to
        // reporting but useful if we ever need to prove the exemption
        // was applied against a legitimate 15% product.
        original_unit_price: Number(it.unit_price),
        original_total: Number(it.total),
        unit_price: Math.round(strippedUnit * 100) / 100,
        total: Math.round(strippedTotal * 100) / 100,
        vat_exemption_applied: true,
        vat_exempt_price_used: special > 0,
      };
    }
    return { ...it, vat_rate };
  });

  // Calculate customer discount on eligible items.
  // Staff discount (per-product) takes precedence and REPLACES any
  // customer-level / additional / promo discount for that specific line.
  // For customer-conditional VAT-exempt lines the base is stripped of the
  // 15% VAT first, so the % is applied on the excl-VAT price the customer
  // actually pays.
  //
  // SAFETY NETS mirror Sales.jsx: cancel the discount on any line where
  //   • markup ≤ 7%, or
  //   • the discount would push the line under cost.
  // Both figures normalised to excl-VAT so 15% VAT doesn't mask a breach.
  const THIN_MARKUP_THRESHOLD_PCT = 7;
  const getLineCost = (item) => {
    if (!item) return 0;
    const candidates = [
      item.cost_price,
      item.last_cost_price_excl_vat,
      item.average_cost_price_excl_vat,
      item.cost_price_excl_vat,
    ]
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0);
    return candidates.length ? Math.max(...candidates) : 0;
  };
  const getLineSellingExcl = (item) => {
    const rate = Number(item?.vat_rate != null ? item.vat_rate : 15) / 100 || 0;
    return item.unit_price / (1 + rate);
  };
  const lineHasThinMarkup = (item) => {
    const cost = getLineCost(item);
    if (cost <= 0) return false;
    const sellingExcl = getLineSellingExcl(item);
    // Round to 2dp so a floating-point 7.0000...001 still trips the guard.
    const markup = Math.round(((sellingExcl - cost) / cost) * 10000) / 100;
    return markup <= THIN_MARKUP_THRESHOLD_PCT;
  };
  const lineWouldGoBelowCost = (item, pct) => {
    const cost = getLineCost(item);
    if (cost <= 0) return false;
    return getLineSellingExcl(item) * (1 - pct / 100) < cost;
  };

  const calculateCustomerDiscount = () => {
    return cart.reduce((sum, item) => {
      const rawTotal = item.unit_price * item.quantity;
      // Base for % discount: prefer the special VAT-Exempt Selling Price
      // (already ex-VAT via /1.15) when applicable — matches what the
      // Sales.jsx totals now do so the two screens agree.
      let itemTotal;
      if (isConditionallyExempt(item)) {
        const special = Number(item.vat_exempt_price_incl_vat) || 0;
        if (special > 0) {
          const discountFactor = 1 - (Number(item.discount_percent) || 0) / 100;
          itemTotal = (special / 1.15) * item.quantity * discountFactor;
        } else {
          itemTotal = rawTotal / 1.15;
        }
      } else {
        itemTotal = rawTotal;
      }
      const staffPct = selectedCustomer?.is_staff ? (Number(item.staff_discount_percent) || 0) : 0;
      if (staffPct > 0) {
        return sum + (itemTotal * staffPct / 100);
      }
      if (skipCustomerDiscount) return sum;
      if (customerDiscountPercent <= 0) return sum;
      // Skip items marked as no_discount
      if (item.no_discount) return sum;
      // Skip items on promotion — a promotional price already IS the
      // discounted price, so the customer's auto-discount must not stack
      // on top of it.
      if (item.plu_code && promoPluCodes.has(item.plu_code)) return sum;
      // Thin-markup and below-cost safety nets (mirror Sales.jsx).
      if (lineHasThinMarkup(item)) return sum;
      if (lineWouldGoBelowCost(item, customerDiscountPercent)) return sum;
      return sum + (itemTotal * customerDiscountPercent / 100);
    }, 0);
  };

  const customerDiscount = calculateCustomerDiscount();
  const itemDiscount = totals.totalDiscount || cart.reduce((sum, item) => sum + (item.unit_price * item.quantity * (item.discount_percent || 0) / 100), 0);
  const totalDiscount = itemDiscount + customerDiscount + promotionDiscount;

  const subtotal = totals.subtotalExclVAT;
  const tax = totals.vat;
  // Apply customer discount and promotion discount to total
  const total = totals.totalInclVAT - customerDiscount - promotionDiscount;

  const roundToNearest10c = (amount) => {
    return Math.round(amount / 0.1) * 0.1;
  };

  const roundedTotal = roundToNearest10c(total);

  // Yard/Outside stock detection: if the cart contains any product flagged
  // as `is_yard_stock`, the till must print 2 copies of the receipt so the
  // yard staff can physically confirm the customer paid before loading.
  // Otherwise print only 1 (default policy to save thermal paper).
  const hasYardStockItem = cart.some((it) => it.is_yard_stock);
  const receiptCopies = hasYardStockItem ? 2 : 1;

  // Check if customer has sufficient credit for account sale
  const getAvailableCredit = () => {
    if (!selectedCustomer) return 0;
    // Use the new effective_credit_limit which includes any credit balance
    // If effective_credit_limit is available, use it; otherwise fall back to old calculation
    if (selectedCustomer.effective_credit_limit !== undefined && selectedCustomer.effective_credit_limit !== null) {
      // Use the pre-calculated available_credit if available
      if (selectedCustomer.available_credit !== undefined && selectedCustomer.available_credit !== null) {
        return selectedCustomer.available_credit;
      }
      const currentBalance = selectedCustomer.calculated_balance ?? selectedCustomer.account_balance ?? 0;
      return selectedCustomer.effective_credit_limit - currentBalance;
    }
    // Fallback for old data without effective_credit_limit
    const creditLimit = selectedCustomer.credit_limit || 0;
    const currentBalance = selectedCustomer.calculated_balance ?? selectedCustomer.account_balance ?? 0;
    // If customer has credit (negative balance), they can use it
    const customerCredit = Math.max(0, -currentBalance);
    return (creditLimit + customerCredit) - currentBalance;
  };

  const hasSufficientCredit = () => {
    if (transactionType !== "ACCOUNT SALE" || !selectedCustomer) return true;
    
    const currentBalance = selectedCustomer.calculated_balance ?? selectedCustomer.account_balance ?? 0;
    const creditLimit = selectedCustomer.credit_limit || 0;
    const customerCredit = Math.max(0, -currentBalance); // Credit balance if negative
    const effectiveLimit = selectedCustomer.effective_credit_limit ?? (creditLimit + customerCredit);
    
    // Allow if customer has credit balance OR a configured credit limit
    if (effectiveLimit <= 0 && customerCredit <= 0) return false;
    
    // Check if the sale amount is within available credit
    return getAvailableCredit() >= roundedTotal;
  };

  useEffect(() => {
    if (selectedPaymentMethod === "CASH") {
      setPaymentAmount("");
    } else if (selectedPaymentMethod === "LOYALTY_CREDIT") {
      // Cap loyalty pre-fill at the customer's available balance — otherwise
      // typing the customer's loyalty as a single payment exceeds what they have.
      // Sub-fix for: clicking LOYALTY pre-filled 280 when only 123.90 available.
      const remaining = roundedTotal - getTotalPaid();
      const loyaltyAvailable = selectedCustomer?.loyalty_credit_balance || 0;
      const target = remaining > 0 ? remaining : roundedTotal;
      setPaymentAmount(Math.min(target, loyaltyAvailable).toFixed(2));
    } else {
      const remaining = roundedTotal - getTotalPaid();
      setPaymentAmount(remaining > 0 ? remaining.toFixed(2) : roundedTotal.toFixed(2));
    }
  }, [selectedPaymentMethod, roundedTotal, selectedCustomer?.loyalty_credit_balance]);

  useEffect(() => {
    if (transactionType === "ACCOUNT SALE" || transactionType === "ACCOUNT REFUND" || transactionType === "CUSTOMER SALE") {
      fetchCustomers();
      // If a customer is already selected and we switch to ACCOUNT SALE, refresh their data
      if (transactionType === "ACCOUNT SALE" && selectedCustomer && isOnline) {
        refreshSelectedCustomer();
      }
    }
  }, [transactionType]);

  // When modal opens with a selected customer and ACCOUNT SALE, refresh the customer data
  useEffect(() => {
    if (open && transactionType === "ACCOUNT SALE" && selectedCustomer && isOnline) {
      refreshSelectedCustomer();
    }
  }, [open]);

  // Pre-select the customer chosen on the cashier screen (Sales.jsx customer
  // bar). Switching to CUSTOMER SALE so the customer block is visible — the
  // cashier can still pick a different transaction type after the modal opens.
  useEffect(() => {
    if (open && initialCustomer) {
      setSelectedCustomer(initialCustomer);
      setTransactionType((prev) => (prev === "CASH SALE" ? "CUSTOMER SALE" : prev));
    }
  }, [open, initialCustomer]);

  // Pre-tick the "email invoice" checkbox based on this customer's last 3 sales.
  // Heuristic: if ≥2 of the last 3 ACCOUNT/CUSTOMER sales were emailed, default ON.
  // Cashier can always override per sale.
  useEffect(() => {
    if (!selectedCustomer?.id || !isOnline) return;
    if (transactionType !== "ACCOUNT SALE" && transactionType !== "CUSTOMER SALE") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/customers/${selectedCustomer.id}/email-default-preference`
        );
        if (!cancelled && typeof res.data?.should_email_default === "boolean") {
          setEmailInvoiceToCustomer(res.data.should_email_default);
        }
      } catch (e) {
        // Non-fatal — fall back to default OFF.
        console.debug("email-default-preference fetch failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCustomer?.id, transactionType, isOnline]);

  // Refresh the selected customer's data to get latest balance
  const refreshSelectedCustomer = async () => {
    if (!selectedCustomer?.id) return;
    try {
      const response = await axios.get(`${API}/customers/${selectedCustomer.id}`);
      if (response.data) {
        setSelectedCustomer(response.data);
      }
    } catch (error) {
      console.error("Error refreshing customer data:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      if (isOnline) {
        const response = await axios.get(`${API}/customers`, {
          params: {
            include_calculated_balance: true,  // Use calculated balance for accurate display
            limit: 2000,
          }
        });
        // Sort customers alphabetically by name
        const sortedCustomers = (response.data || []).sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setCustomers(sortedCustomers);
      } else {
        // When offline, load customers from cache
        const cachedCustomers = await getCachedCustomers();
        if (cachedCustomers && cachedCustomers.length > 0) {
          // Sort cached customers alphabetically
          const sortedCustomers = cachedCustomers.sort((a, b) => 
            (a.name || '').localeCompare(b.name || '')
          );
          setCustomers(sortedCustomers);
          toast.info(`Loaded ${cachedCustomers.length} customers from offline cache`);
        } else {
          toast.warning("No customers cached. Update cache when online.");
        }
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      // Try cached customers as fallback
      try {
        const cachedCustomers = await getCachedCustomers();
        if (cachedCustomers && cachedCustomers.length > 0) {
          // Sort cached customers alphabetically
          const sortedCustomers = cachedCustomers.sort((a, b) => 
            (a.name || '').localeCompare(b.name || '')
          );
          setCustomers(sortedCustomers);
          toast.info("Loaded customers from offline cache");
        } else {
          toast.error("Failed to load customers");
        }
      } catch (cacheError) {
        toast.error("Failed to load customers");
      }
    }
  };

  // Function to send invoice email
  const sendInvoiceEmail = async (saleId, email) => {
    try {
      setSendingEmail(true);
      const response = await axios.post(`${API}/email/sale-invoice`, {
        sale_id: saleId,
        recipient_email: email
      });
      
      // Check if email was blocked due to global settings
      if (response.data?.blocked_reason === "emails_disabled_in_settings") {
        // Silently skip - don't show error since it's expected
        console.log("Email sending is disabled in settings");
        return false;
      }
      
      toast.success(`Invoice emailed to ${email}`);
      return true;
    } catch (error) {
      console.error("Error sending invoice email:", error);
      // Only show error if it's not because emails are disabled
      if (!error.response?.data?.blocked_reason) {
        toast.error("Failed to send invoice email");
      }
      return false;
    } finally {
      setSendingEmail(false);
    }
  };

  // Function to send refund/credit note email
  const sendRefundEmail = async (refundId, email) => {
    try {
      setSendingEmail(true);
      const response = await axios.post(`${API}/email/refund`, {
        refund_id: refundId,
        recipient_email: email
      });
      
      // Check if email was blocked due to global settings
      if (response.data?.blocked_reason === "emails_disabled_in_settings") {
        console.log("Email sending is disabled in settings");
        return false;
      }
      
      toast.success(`Credit note emailed to ${email}`);
      return true;
    } catch (error) {
      console.error("Error sending refund email:", error);
      if (!error.response?.data?.blocked_reason) {
        toast.error("Failed to send credit note email");
      }
      return false;
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle auto-send email after account sale
  const handleAutoSendEmail = async (sale, customer) => {
    // First check if emails are enabled globally
    try {
      const settingsResponse = await axios.get(`${API}/settings/email`);
      if (!settingsResponse.data?.emails_enabled) {
        // Emails are disabled - silently skip
        return;
      }
    } catch (error) {
      // If we can't fetch settings, proceed with email attempt
      console.log("Could not fetch email settings, proceeding with email");
    }
    
    // Check customer-level auto_email preference
    if (customer?.auto_email === false) {
      console.log(`Auto-email disabled for customer ${customer.name}`);
      return;
    }
    
    if (customer?.email) {
      // Customer has email - send automatically
      await sendInvoiceEmail(sale.id, customer.email);
    } else {
      // No email - show prompt
      setPendingSaleForEmail(sale);
      setEmailPromptAddress("");
      setShowEmailPrompt(true);
    }
  };

  // Handle auto-send email after account refund
  const handleAutoSendRefundEmail = async (refund, customer) => {
    // First check if emails are enabled globally
    try {
      const settingsResponse = await axios.get(`${API}/settings/email`);
      if (!settingsResponse.data?.emails_enabled) {
        // Emails are disabled - silently skip
        return;
      }
    } catch (error) {
      // If we can't fetch settings, proceed with email attempt
      console.log("Could not fetch email settings, proceeding with email");
    }
    
    // Check customer-level auto_email preference
    if (customer?.auto_email === false) {
      console.log(`Auto-email disabled for customer ${customer.name}`);
      return;
    }
    
    if (customer?.email) {
      // Customer has email - send automatically
      await sendRefundEmail(refund.id, customer.email);
    }
    // For refunds, we don't show prompt if no email - just skip
  };

  // Handle email prompt submit
  const handleEmailPromptSubmit = async () => {
    if (!emailPromptAddress || !pendingSaleForEmail) return;
    
    const success = await sendInvoiceEmail(pendingSaleForEmail.id, emailPromptAddress);
    if (success) {
      // Optionally update customer's email in the system
      if (selectedCustomer && !selectedCustomer.email) {
        try {
          await axios.put(`${API}/customers/${selectedCustomer.id}`, {
            email: emailPromptAddress
          });
          toast.info("Customer email saved for future invoices");
        } catch (error) {
          console.error("Failed to save customer email:", error);
        }
      }
    }
    setShowEmailPrompt(false);
    setPendingSaleForEmail(null);
  };

  // Handle email prompt cancel
  const handleEmailPromptCancel = () => {
    setShowEmailPrompt(false);
    setPendingSaleForEmail(null);
    toast.info("Invoice not emailed");
  };

  const paymentMethods = [
    { value: "CASH", label: "CASH" },
    { value: "CARD", label: "CARD" },
    { value: "EFT", label: "EFT" },
    { value: "VOUCHER", label: "VOUCHER" },
    { value: "GIFT_VOUCHER", label: "GIFT VOUCHER" },
    ...(selectedCustomer ? [{ value: "LOYALTY_CREDIT", label: "LOYALTY", disabled: !(selectedCustomer.loyalty_credit_balance > 0) }] : []),
  ];

  // Spend-reward vouchers are CASH/CARD ONLY. When the cashier switches
  // the transaction type to ACCOUNT SALE / ACCOUNT REFUND, strip the
  // auto-added voucher line from the cart before ringing it up.
  const handleTransactionTypeChange = (newType) => {
    const isAccountType = newType === "ACCOUNT SALE" || newType === "ACCOUNT REFUND";
    if (isAccountType && cart.some((i) => i.is_voucher_reward)) {
      onVoucherIneligible();
      toast.info("Voucher removed — spend-reward vouchers are cash/card sales only.");
    }
    setTransactionType(newType);
  };

  const handlePaymentMethodClick = (method) => {
    setSelectedPaymentMethod(method);
    if (method === "CASH") {
      setPaymentAmount("");
    } else if (method === "LOYALTY_CREDIT") {
      // Auto-fill with loyalty balance or remaining amount, whichever is less
      const remaining = roundedTotal - getTotalPaid();
      const loyaltyAvailable = selectedCustomer?.loyalty_credit_balance || 0;
      setPaymentAmount(Math.min(remaining > 0 ? remaining : roundedTotal, loyaltyAvailable).toFixed(2));
    } else {
      const remaining = roundedTotal - getTotalPaid();
      setPaymentAmount(remaining > 0 ? remaining.toFixed(2) : roundedTotal.toFixed(2));
    }
  };

  const handleAddPayment = () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    // Validate loyalty credit against available balance
    if (selectedPaymentMethod === "LOYALTY_CREDIT") {
      const loyaltyAvailable = selectedCustomer?.loyalty_credit_balance || 0;
      const existingLoyalty = payments.filter(p => p.method === "LOYALTY_CREDIT").reduce((sum, p) => sum + p.amount, 0);
      if (amount + existingLoyalty > loyaltyAvailable) {
        toast.error(`Loyalty credit (R ${(amount + existingLoyalty).toFixed(2)}) exceeds available balance (R ${loyaltyAvailable.toFixed(2)})`);
        return;
      }
    }
    
    setPayments([...payments, { method: selectedPaymentMethod, amount }]);
    
    if (selectedPaymentMethod === "CASH") {
      setPaymentAmount("");
    } else {
      const remaining = roundedTotal - getTotalPaid() - amount;
      setPaymentAmount(remaining > 0 ? remaining.toFixed(2) : "0.00");
    }
  };

  const handleRemovePayment = (index) => {
    const newPayments = payments.filter((_, i) => i !== index);
    setPayments(newPayments);
    
    if (selectedPaymentMethod === "CASH") {
      setPaymentAmount("");
    } else {
      const remaining = roundedTotal - newPayments.reduce((sum, p) => sum + p.amount, 0);
      setPaymentAmount(remaining > 0 ? remaining.toFixed(2) : "0.00");
    }
  };

  const getTotalPaid = () => {
    return payments.reduce((sum, p) => sum + p.amount, 0);
  };

  const getChange = () => {
    const paid = getTotalPaid();
    const voucherChange = payments.reduce((sum, p) => sum + (p.cash_change || 0), 0);
    return (paid > total ? paid - total : 0) + voucherChange;
  };

  const addGiftVoucherPayment = (p) => {
    const next = [...payments, p];
    setPayments(next);
    const remaining = roundedTotal - next.reduce((sum, x) => sum + x.amount, 0);
    setSelectedPaymentMethod("CASH");
    setPaymentAmount("");
    toast.success(`Gift voucher R ${p.amount.toFixed(2)} added${remaining > 0.009 ? ` — R ${remaining.toFixed(2)} still due` : ""}`);
  };
  const hasGiftVoucher = payments.some((p) => p.method === "GIFT_VOUCHER");

  // Handle split payment confirmation from modal
  const handleSplitPaymentConfirm = (splitPayments, change) => {
    setPayments(splitPayments);
    toast.success(`Split payment added: ${splitPayments.map(p => `${p.method} R${p.amount.toFixed(2)}`).join(", ")}`);
  };

  // True when the current customer requires an order number and the
  // cashier has neither entered one nor obtained a manager PIN override.
  const orderNumberBlocked =
    !!selectedCustomer?.requires_order_number &&
    !customerOrderNumber.trim() &&
    !orderNumberOverrideBy;

  const submitOrderNumberOverride = async (e) => {
    e?.preventDefault?.();
    if (orderNumberPinBusy) return;
    if (!orderNumberPin || orderNumberPin.length < 4) {
      setOrderNumberPinError("Enter a 4-digit PIN");
      return;
    }
    setOrderNumberPinBusy(true);
    setOrderNumberPinError("");
    try {
      const res = await axios.post(`${API}/auth/verify-pin-override`, {
        pin: orderNumberPin,
        reason: "skip_customer_order_number",
        context: {
          customer_id: selectedCustomer?.id,
          customer_name: selectedCustomer?.name,
          cart_total: roundedTotal,
        },
      });
      const approver = res.data.user_name;
      setOrderNumberOverrideBy(approver);
      setOrderNumberPin("");
      setOrderNumberPinBusy(false);
      setOrderNumberPinOpen(false);
      toast.success(`Order number override approved by ${approver}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid PIN";
      setOrderNumberPinError(msg);
      setOrderNumberPinBusy(false);
      setOrderNumberPin("");
    }
  };

  const handleCompleteSale = async () => {
    // Synchronous double-submit guard — see inFlightRef declaration.
    // Must run BEFORE anything that touches state so a rapid-fire
    // second click bounces immediately.
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    // HARD-BLOCK: Customer requires an order number. Manager can override
    // via PIN — that sets orderNumberOverrideBy and unblocks this check.
    if (
      selectedCustomer?.requires_order_number &&
      !customerOrderNumber.trim() &&
      !orderNumberOverrideBy
    ) {
      toast.error(
        `${selectedCustomer.name} requires an order number. Enter one or use PIN override.`
      );
      setOrderNumberPinOpen(true);
      inFlightRef.current = false;
      return;
    }
    
    // SELF USE - zero-value transaction, just reduces stock
    if (transactionType === "SELF USE") {
      setProcessing(true);
      inFlightRef.current = true;
      
      const saleData = {
        transaction_type: "SELF USE",
        items: cartWithResolvedVat(),
        subtotal: 0,
        discount: 0,
        customer_discount: 0,
        customer_discount_percent: 0,
        tax: 0,
        total: 0,
        payments: [{ method: "SELF USE", amount: 0 }],
        cashier: cashierName,
        salesperson_id: salesperson?.id || null,
        salesperson_name: salesperson?.name || null,
        salesperson_marketing_number: salesperson?.marketing_number || null,
        price_level: priceLevel,
        note: saleNote.trim() || null,
        idempotency_key: idempotencyKeyRef.current || mintIdempotencyKey()
      };

      try {
        const response = await axios.post(`${API}/sales/transaction`, saleData);
        toast.success(`Self-use recorded: ${cart.length} item(s). Stock reduced.`);
        setCompletedTransaction(response.data);
        if (onComplete) onComplete(response.data);
      } catch (error) {
        console.error("Self-use error:", error);
        toast.error(error?.response?.data?.detail || "Failed to record self-use");
      }
      
      setProcessing(false);
      inFlightRef.current = false;
      return;
    }
    
    // For account sales, no payment details needed - just charge to account
    if (transactionType === "ACCOUNT SALE") {
      if (!selectedCustomer) {
        toast.error("Please select a customer for account sale");
        inFlightRef.current = false;
        return;
      }
      
      // REAL-TIME credit check using selected customer ID
      if (isOnline) {
        try {
          const freshCustomerResponse = await axios.get(`${API}/customers/${selectedCustomer.id}`);
          const freshCustomer = freshCustomerResponse.data;
          const freshBalance = freshCustomer.calculated_balance ?? freshCustomer.account_balance ?? 0;
          const freshCreditLimit = freshCustomer.credit_limit || 0;
          const freshCustomerCredit = Math.max(0, -freshBalance); // Credit balance if negative
          const freshEffectiveLimit = freshCustomer.effective_credit_limit ?? (freshCreditLimit + freshCustomerCredit);
          const freshAvailableCredit = freshEffectiveLimit - freshBalance;

          // Allow if customer has credit balance OR a configured credit limit
          if (freshEffectiveLimit <= 0 && freshCustomerCredit <= 0) {
            toast.error(`Customer ${selectedCustomer.name} has no credit facility and no credit balance. Account sale is not allowed.`);
            return;
          }

          if (freshAvailableCredit < roundedTotal) {
            toast.error(`Insufficient credit for customer ${selectedCustomer.name}. Available credit: R${freshAvailableCredit.toFixed(2)}, Transaction amount: R${roundedTotal.toFixed(2)}`);
            return;
          }
        } catch (error) {
          console.error("Error checking credit:", error);
          // Fall back to cached data
          if (!hasSufficientCredit()) {
            toast.error(`Insufficient credit. Available: R ${getAvailableCredit().toFixed(2)}, Required: R ${roundedTotal.toFixed(2)}`);
            return;
          }
        }
      } else {
        // Offline - use cached data
        if (!hasSufficientCredit()) {
          toast.error(`Insufficient credit. Available: R ${getAvailableCredit().toFixed(2)}, Required: R ${roundedTotal.toFixed(2)}`);
          return;
        }
      }
      
      setProcessing(true);
      inFlightRef.current = true;
      
      const saleData = {
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,
        customer_account_number: selectedCustomer.account_number || null,
        customer_address: selectedCustomer.street_address || null,
        customer_suburb: selectedCustomer.suburb || null,
        customer_province: selectedCustomer.province || null,
        customer_contact: selectedCustomer.contact_number || null,
        customer_vat_number: selectedCustomer.vat_number || null,
        customer_email: selectedCustomer.email || null,
        customer_order_number: customerOrderNumber.trim() || null,
        transaction_type: "ACCOUNT SALE",
        items: cartWithResolvedVat(),
        subtotal: subtotal,
        discount: totalDiscount,
        customer_discount: customerDiscount,
        customer_discount_percent: effectiveCustomerDiscountPercent,
        tax: tax,
        total: roundedTotal,
        payments: [{ method: "ACCOUNT", amount: roundedTotal }],
        cashier: cashierName,
        salesperson_id: salesperson?.id || null,
        salesperson_name: salesperson?.name || null,
        salesperson_marketing_number: salesperson?.marketing_number || null,
        price_level: priceLevel,
        note: saleNote.trim() || null,
        allow_below_cost: allowBelowCost || undefined,
        idempotency_key: idempotencyKeyRef.current || mintIdempotencyKey()
      };

      try {
        // Try online sale first (with hard timeout to avoid hanging when WiFi
        // is up but the internet is down).
        if (isOnline) {
          const response = await axios.post(`${API}/sales/transaction`, saleData, { timeout: 8000 });
          
          toast.success(`Sale charged to ${selectedCustomer.name}'s account (${selectedCustomer.account_number})`);
          setCompletedTransaction(response.data);
          setShowReceipt(true);
          
          // Email invoice only when the cashier explicitly ticked the option.
          if (emailInvoiceToCustomer) {
            await handleAutoSendEmail(response.data, selectedCustomer);
          }
        } else {
          // Offline mode - queue the account sale
          const offlineSale = await queueOfflineSale(saleData);
          
          // Create a mock transaction for receipt (marked as pending sync)
          const mockTransaction = {
            ...saleData,
            id: offlineSale.offline_id,
            created_at: new Date().toISOString(),
            invoice_number: `OFFLINE-${Date.now()}`,
            is_offline_sale: true,
            pending_sync: true,
          };
          
          const pendingCount = await getPendingOfflineSalesCount();
          
          toast.success(
            <div className="flex items-center gap-2">
              <WifiOff className="h-4 w-4" />
              <span>Account sale saved offline ({pendingCount} pending sync)</span>
            </div>,
            { duration: 5000 }
          );
          
          setCompletedTransaction(mockTransaction);
          setShowReceipt(true);
        }
      } catch (error) {
        console.error("Error completing sale:", error);
        
        // If online sale fails due to network or timeout, try to queue offline
        const isNetworkLike =
          error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNABORTED' ||
          (error.message || '').toLowerCase().includes('timeout') ||
          !navigator.onLine;
        if (isNetworkLike) {
          try {
            const offlineSale = await queueOfflineSale(saleData);
            
            const mockTransaction = {
              ...saleData,
              id: offlineSale.offline_id,
              created_at: new Date().toISOString(),
              invoice_number: `OFFLINE-${Date.now()}`,
              is_offline_sale: true,
              pending_sync: true,
            };
            
            const pendingCount = await getPendingOfflineSalesCount();
            
            toast.warning(
              <div className="flex items-center gap-2">
                <WifiOff className="h-4 w-4" />
                <span>Connection lost - Account sale saved offline ({pendingCount} pending)</span>
              </div>,
              { duration: 5000 }
            );
            
            setCompletedTransaction(mockTransaction);
            setShowReceipt(true);
          } catch (offlineError) {
            toast.error("Failed to save sale. Please try again.");
          }
        } else {
          toast.error(error.response?.data?.detail || "Failed to complete sale");
        }
      } finally {
        setProcessing(false);
      inFlightRef.current = false;
      }
      return;
    }

    // For ACCOUNT REFUND - credit back to customer account
    if (transactionType === "ACCOUNT REFUND") {
      if (!selectedCustomer) {
        toast.error("Please select a customer for account refund");
        return;
      }
      if ((refundReason || "").trim().length < 3) {
        toast.error("Please enter a reason for the refund (at least 3 characters)");
        return;
      }
      
      setProcessing(true);
      inFlightRef.current = true;
      
      try {
        const refundData = {
          customer_id: selectedCustomer.id,
          customer_name: selectedCustomer.name,
          customer_account_number: selectedCustomer.account_number || null,
          customer_address: selectedCustomer.street_address || null,
          customer_suburb: selectedCustomer.suburb || null,
          customer_province: selectedCustomer.province || null,
          customer_contact: selectedCustomer.contact_number || null,
          customer_vat_number: selectedCustomer.vat_number || null,
          customer_email: selectedCustomer.email || null,
          customer_order_number: customerOrderNumber.trim() || null,
          transaction_type: "ACCOUNT REFUND",
          items: cartWithResolvedVat(),
          subtotal: subtotal,
          discount: 0,
          tax: tax,
          total: roundedTotal,
          payments: [{ method: "ACCOUNT CREDIT", amount: roundedTotal }],
          cashier: cashierName,
          salesperson_id: salesperson?.id || null,
          salesperson_name: salesperson?.name || null,
          salesperson_marketing_number: salesperson?.marketing_number || null,
          price_level: priceLevel,
          refund_reason: refundReason.trim()
        };

        const response = await saveWithRetry(() => axios.post(`${API}/refunds/transaction`, refundData));
        
        toast.success(`Refund of R ${roundedTotal.toFixed(2)} credited to ${selectedCustomer.name}'s account`);
        setCompletedTransaction(response.data);
        setShowReceipt(true);
        
        // Auto-send credit note email for account refunds
        await handleAutoSendRefundEmail(response.data, selectedCustomer);
      } catch (error) {
        console.error("Error processing refund:", error);
        toast.error(errorMessage(error, "Failed to process refund"));
      } finally {
        setProcessing(false);
      inFlightRef.current = false;
      }
      return;
    }

    // For CASH REFUND - give cash back
    if (transactionType === "CASH REFUND") {
      if ((refundReason || "").trim().length < 3) {
        toast.error("Please enter a reason for the refund (at least 3 characters)");
        return;
      }
      setProcessing(true);
      inFlightRef.current = true;
      
      try {
        const refundData = {
          customer_id: selectedCustomer?.id || null,
          customer_name: selectedCustomer?.name || null,
          customer_account_number: selectedCustomer?.account_number || null,
          customer_address: selectedCustomer?.street_address || null,
          customer_suburb: selectedCustomer?.suburb || null,
          customer_province: selectedCustomer?.province || null,
          customer_contact: selectedCustomer?.contact_number || null,
          customer_vat_number: selectedCustomer?.vat_number || null,
          customer_email: selectedCustomer?.email || null,
          customer_order_number: customerOrderNumber.trim() || null,
          transaction_type: "CASH REFUND",
          items: cartWithResolvedVat(),
          subtotal: subtotal,
          discount: 0,
          tax: tax,
          total: roundedTotal,
          payments: [{ method: "CASH", amount: roundedTotal }],
          cashier: cashierName,
          salesperson_id: salesperson?.id || null,
          salesperson_name: salesperson?.name || null,
          salesperson_marketing_number: salesperson?.marketing_number || null,
          price_level: priceLevel,
          refund_reason: refundReason.trim()
        };

        const response = await saveWithRetry(() => axios.post(`${API}/refunds/transaction`, refundData));
        
        toast.success(`Cash refund of R ${roundedTotal.toFixed(2)} processed. Stock updated.`);
        setCompletedTransaction(response.data);
        setShowReceipt(true);
        
        // Auto-send credit note email for cash refunds if customer has email
        if (selectedCustomer) {
          await handleAutoSendRefundEmail(response.data, selectedCustomer);
        }
      } catch (error) {
        console.error("Error processing refund:", error);
        toast.error(errorMessage(error, "Failed to process refund"));
      } finally {
        setProcessing(false);
      inFlightRef.current = false;
      }
      return;
    }

    // For CARD REFUND - refund to card
    if (transactionType === "CARD REFUND") {
      if ((refundReason || "").trim().length < 3) {
        toast.error("Please enter a reason for the refund (at least 3 characters)");
        return;
      }
      setProcessing(true);
      inFlightRef.current = true;
      
      try {
        const refundData = {
          customer_id: selectedCustomer?.id || null,
          customer_name: selectedCustomer?.name || null,
          customer_account_number: selectedCustomer?.account_number || null,
          customer_address: selectedCustomer?.street_address || null,
          customer_suburb: selectedCustomer?.suburb || null,
          customer_province: selectedCustomer?.province || null,
          customer_contact: selectedCustomer?.contact_number || null,
          customer_vat_number: selectedCustomer?.vat_number || null,
          customer_email: selectedCustomer?.email || null,
          customer_order_number: customerOrderNumber.trim() || null,
          transaction_type: "CARD REFUND",
          items: cartWithResolvedVat(),
          subtotal: subtotal,
          discount: 0,
          tax: tax,
          total: roundedTotal,
          payments: [{ method: "CARD", amount: roundedTotal }],
          cashier: cashierName,
          salesperson_id: salesperson?.id || null,
          salesperson_name: salesperson?.name || null,
          salesperson_marketing_number: salesperson?.marketing_number || null,
          price_level: priceLevel,
          refund_reason: refundReason.trim()
        };

        const response = await saveWithRetry(() => axios.post(`${API}/refunds/transaction`, refundData));
        
        toast.success(`Card refund of R ${roundedTotal.toFixed(2)} processed. Stock updated.`);
        setCompletedTransaction(response.data);
        setShowReceipt(true);
        
        // Auto-send credit note email for card refunds if customer has email
        if (selectedCustomer) {
          await handleAutoSendRefundEmail(response.data, selectedCustomer);
        }
      } catch (error) {
        console.error("Error processing refund:", error);
        toast.error(errorMessage(error, "Failed to process refund"));
      } finally {
        setProcessing(false);
      inFlightRef.current = false;
      }
      return;
    }
    
    // For cash/card sales, continue with normal payment flow
    // For CUSTOMER SALE, require customer to be selected
    if (transactionType === "CUSTOMER SALE" && !selectedCustomer) {
      toast.error("Please select a customer for Customer Sale");
      return;
    }
    
    let finalPayments = [...payments];
    
    // If there's an amount entered but not added to payments, add it automatically
    if (paymentAmount && parseFloat(paymentAmount) > 0 && payments.length === 0 && selectedPaymentMethod !== "GIFT_VOUCHER") {
      finalPayments = [{ method: selectedPaymentMethod, amount: parseFloat(paymentAmount) }];
    }
    // Remaining due after a gift voucher, typed but not yet added: add it now.
    if (paymentAmount && parseFloat(paymentAmount) > 0 && payments.length > 0
        && payments.every((p) => p.method === "GIFT_VOUCHER") && selectedPaymentMethod !== "GIFT_VOUCHER") {
      finalPayments = [...payments, { method: selectedPaymentMethod, amount: parseFloat(paymentAmount) }];
    }
    const usesGiftVoucher = finalPayments.some((p) => p.method === "GIFT_VOUCHER");
    if (usesGiftVoucher && !isOnline) {
      toast.error("Gift vouchers need an internet connection");
      return;
    }
    
    const totalPaid = finalPayments.reduce((sum, p) => sum + p.amount, 0);
    
    // Use small tolerance for floating point comparison
    if (totalPaid < roundedTotal - 0.01) {
      toast.error(`Insufficient payment. Still need R ${(roundedTotal - totalPaid).toFixed(2)}`);
      return;
    }

    setProcessing(true);

    const saleData = {
      customer_id: selectedCustomer?.id || null,
      customer_name: selectedCustomer?.name || null,
      customer_account_number: selectedCustomer?.account_number || null,
      customer_address: selectedCustomer?.street_address || null,
      customer_suburb: selectedCustomer?.suburb || null,
      customer_province: selectedCustomer?.province || null,
      customer_contact: selectedCustomer?.contact_number || null,
      customer_vat_number: selectedCustomer?.vat_number || null,
      customer_email: selectedCustomer?.email || null,
      customer_order_number: customerOrderNumber.trim() || null,
      transaction_type: transactionType,
      items: cartWithResolvedVat(),
      subtotal: subtotal,
      discount: totalDiscount,
      customer_discount: customerDiscount,
      customer_discount_percent: effectiveCustomerDiscountPercent,
      promotion_discount: promotionDiscount,
      applied_promotions: appliedPromotions,
      tax: tax,
      total: roundedTotal,
      payments: finalPayments,
      cashier: cashierName,
      salesperson_id: salesperson?.id || null,
      salesperson_name: salesperson?.name || null,
      salesperson_marketing_number: salesperson?.marketing_number || null,
      price_level: priceLevel,
      note: saleNote.trim() || null,
      allow_below_cost: allowBelowCost || undefined,
      idempotency_key: idempotencyKeyRef.current || mintIdempotencyKey()
    };

    try {
      // Try online sale first
      if (isOnline) {
        // Hard timeout (8s). If the request hangs because WiFi is up but the
        // internet uplink is down, axios will throw ECONNABORTED and we fall
        // through to the offline queue below instead of leaving the cashier
        // stuck on a spinner.
        const response = await axios.post(`${API}/sales/transaction`, saleData, { timeout: 8000 });

        toast.success("Sale completed successfully!");
        finalPayments.filter((p) => p.method === "GIFT_VOUCHER").forEach((p) => {
          axios.get(`${API}/gift-vouchers/lookup/${p.reference}`).then(({ data }) => {
            if (data.balance > 0) toast.info(`Gift voucher ${data.code} has R ${data.balance.toFixed(2)} left`, { duration: 8000 });
          }).catch(() => {});
        });
        setCompletedTransaction(response.data);
        setShowReceipt(true);

        // Email invoice for CUSTOMER SALE only when the cashier explicitly ticked the option.
        if (transactionType === "CUSTOMER SALE" && selectedCustomer && emailInvoiceToCustomer) {
          await handleAutoSendEmail(response.data, selectedCustomer);
        }
      } else {
        // Offline mode - queue the sale
        const offlineSale = await queueOfflineSale(saleData);
        
        // Create a mock transaction for receipt (marked as pending sync)
        const mockTransaction = {
          ...saleData,
          id: offlineSale.offline_id,
          created_at: new Date().toISOString(),
          invoice_number: `OFFLINE-${Date.now()}`,
          is_offline_sale: true,
          pending_sync: true,
        };
        
        const pendingCount = await getPendingOfflineSalesCount();
        
        toast.success(
          <div className="flex items-center gap-2">
            <WifiOff className="h-4 w-4" />
            <span>Sale saved offline ({pendingCount} pending sync)</span>
          </div>,
          { duration: 5000 }
        );
        
        setCompletedTransaction(mockTransaction);
        setShowReceipt(true);
      }
    } catch (error) {
      console.error("Error completing sale:", error);
      
      // If online sale fails, try to queue offline.
      // Treat true network errors AND request timeouts (ECONNABORTED, code=ERR_NETWORK,
      // or message contains 'timeout') as "network is dead" — fall through to queue.
      const isNetworkLike =
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        (error.message || '').toLowerCase().includes('timeout') ||
        !isOnline;
      if (isNetworkLike && usesGiftVoucher) {
        toast.error("Connection lost — a sale paid with a gift voucher can't be saved offline. Please try again.");
      } else if (isNetworkLike) {
        try {
          const offlineSale = await queueOfflineSale(saleData);
          
          const mockTransaction = {
            ...saleData,
            id: offlineSale.offline_id,
            created_at: new Date().toISOString(),
            invoice_number: `OFFLINE-${Date.now()}`,
            is_offline_sale: true,
            pending_sync: true,
          };
          
          const pendingCount = await getPendingOfflineSalesCount();
          
          toast.warning(
            <div className="flex items-center gap-2">
              <WifiOff className="h-4 w-4" />
              <span>Connection lost - Sale saved offline ({pendingCount} pending)</span>
            </div>,
            { duration: 5000 }
          );
          
          setCompletedTransaction(mockTransaction);
          setShowReceipt(true);
        } catch (offlineError) {
          toast.error("Failed to save sale. Please try again.");
        }
      } else {
        // Non-network error — surface the backend detail. Special-case the
        // below-cost block: reveal a "Manager approves selling below cost"
        // checkbox so a legit stock-clearance sale can be retried by an
        // authorized user without needing to change Product Master pricing.
        const detail = error.response?.data?.detail || "Failed to complete sale";
        const looksLikeBelowCost = typeof detail === "string" && detail.startsWith("Sale blocked — ") && detail.includes("BELOW COST");
        if (looksLikeBelowCost) {
          setBelowCostBlockDetail(detail);
          toast.error(detail, { duration: 12000 });
        } else {
          toast.error(detail);
        }
      }
    } finally {
      setProcessing(false);
      inFlightRef.current = false;
    }
  };

  const filteredCustomers = customerSearch
    ? customers.filter(c => 
        c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        (c.company && c.company.toLowerCase().includes(customerSearch.toLowerCase())) ||
        (c.account_number && c.account_number.toLowerCase().includes(customerSearch.toLowerCase()))
      )
    : customers;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col" data-testid="checkout-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl">Checkout</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
          {/* Total Due - Compact Display */}
          <div className="bg-gray-700 text-white p-3 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold">TOTAL DUE:</span>
              <span className="text-3xl font-bold">R {roundedTotal.toFixed(2)}</span>
            </div>
            {promotionDiscount > 0 && (
              <div className="flex justify-between items-center text-yellow-400 text-xs mt-1">
                <span>Promotion Discount:</span>
                <span>-R {promotionDiscount.toFixed(2)}</span>
              </div>
            )}
            {customerDiscount > 0 && (
              <div className="flex justify-between items-center text-green-400 text-xs mt-1">
                <span>Customer Discount ({customerDiscountPercent}%):</span>
                <span className="flex items-center gap-3">
                  <span>-R {customerDiscount.toFixed(2)}</span>
                  <button
                    type="button"
                    onClick={() => setSkipCustomerDiscount(true)}
                    className="text-[10px] uppercase tracking-wider text-red-300 hover:text-red-200 underline"
                    data-testid="cancel-customer-discount-btn"
                    title="Cancel discount — items don't qualify"
                  >
                    Cancel
                  </button>
                </span>
              </div>
            )}
            {skipCustomerDiscount && customerDiscountPercent > 0 && (
              <div className="flex justify-between items-center text-amber-300 text-xs mt-1">
                <span>
                  Customer Discount ({customerDiscountPercent}%):{" "}
                  <span className="uppercase tracking-wider text-[10px]">cancelled by cashier</span>
                </span>
                <button
                  type="button"
                  onClick={() => setSkipCustomerDiscount(false)}
                  className="text-[10px] uppercase tracking-wider text-amber-200 hover:text-white underline"
                  data-testid="restore-customer-discount-btn"
                >
                  Restore
                </button>
              </div>
            )}
            {roundedTotal !== total && (
              <div className="text-xs text-gray-300 mt-1">
                Original: R {total.toFixed(2)} | Rounded to nearest 10c
              </div>
            )}
          </div>

          {/* Two Column Layout for Transaction Type and Customer/Payment */}
          <div className="grid grid-cols-2 gap-3">
            {/* Left Column - Transaction Type */}
            <div>
              <Label className="text-xs font-medium mb-1 block">Transaction Type</Label>
              <div className="grid grid-cols-2 gap-1">
                <Button
                  type="button"
                  variant={transactionType === "CASH SALE" ? "default" : "outline"}
                  onClick={() => setTransactionType("CASH SALE")}
                  className={`h-8 text-xs ${transactionType === "CASH SALE" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                  data-testid="transaction-cash-sale"
                >
                  CASH SALE
                </Button>
                <Button
                  type="button"
                  variant={transactionType === "CUSTOMER SALE" ? "default" : "outline"}
                  onClick={() => setTransactionType("CUSTOMER SALE")}
                  className={`h-8 text-xs ${transactionType === "CUSTOMER SALE" ? "bg-blue-600 hover:bg-blue-700" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}
                  data-testid="transaction-customer-sale"
                >
                  CUSTOMER SALE
                </Button>
                <Button
                  type="button"
                  variant={transactionType === "ACCOUNT SALE" ? "default" : "outline"}
                  onClick={() => handleTransactionTypeChange("ACCOUNT SALE")}
                  className={`h-8 text-xs ${transactionType === "ACCOUNT SALE" ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                  data-testid="transaction-account-sale"
                >
                  ACCOUNT SALE
                </Button>
                <Button
                  type="button"
                  variant={transactionType === "CASH REFUND" ? "default" : "outline"}
                  onClick={() => setTransactionType("CASH REFUND")}
                  className={`h-8 text-xs ${transactionType === "CASH REFUND" ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-700 hover:bg-orange-50"}`}
                  data-testid="transaction-cash-refund"
                >
                  CASH REFUND
                </Button>
                <Button
                  type="button"
                  variant={transactionType === "CARD REFUND" ? "default" : "outline"}
                  onClick={() => setTransactionType("CARD REFUND")}
                  className={`h-8 text-xs ${transactionType === "CARD REFUND" ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-700 hover:bg-orange-50"}`}
                  data-testid="transaction-card-refund"
                >
                  CARD REFUND
                </Button>
                <Button
                  type="button"
                  variant={transactionType === "ACCOUNT REFUND" ? "default" : "outline"}
                  onClick={() => handleTransactionTypeChange("ACCOUNT REFUND")}
                  className={`h-8 text-xs ${transactionType === "ACCOUNT REFUND" ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300 text-orange-700 hover:bg-orange-50"}`}
                  data-testid="transaction-account-refund"
                >
                  ACCOUNT REFUND
                </Button>
                <Button
                  type="button"
                  variant={transactionType === "SELF USE" ? "default" : "outline"}
                  onClick={() => setTransactionType("SELF USE")}
                  className={`h-8 text-xs ${transactionType === "SELF USE" ? "bg-teal-600 hover:bg-teal-700 text-white" : "border-teal-300 text-teal-700 hover:bg-teal-50"}`}
                  data-testid="transaction-self-use"
                >
                  SELF USE
                </Button>
              </div>
            </div>

            {/* Right Column - Customer Selection (for Account/Customer types) or Cash Refund Info */}
            <div>
              {(transactionType === "ACCOUNT SALE" || transactionType === "ACCOUNT REFUND" || transactionType === "CUSTOMER SALE") && (
                <div className={`border rounded-lg p-2 h-full ${
                  transactionType === "ACCOUNT REFUND" ? "bg-orange-50 border-orange-200" : 
                  transactionType === "CUSTOMER SALE" ? "bg-blue-50 border-blue-200" : "bg-gray-50"
                }`}>
                  <Label className="text-xs font-medium mb-1 block">
                    {transactionType === "ACCOUNT REFUND" ? "Select Customer to Refund" : 
                     transactionType === "CUSTOMER SALE" ? "Select Customer (Cash/Card Payment)" : "Select Customer"}
                  </Label>
                  <Input
                    placeholder="Search customer or account number..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="mb-1 h-7 text-xs"
                    data-testid="customer-search-input"
                  />
                  <div className="border rounded max-h-20 overflow-y-auto bg-white text-xs">
                    {filteredCustomers.slice(0, 5).map((customer) => {
                      const levelDiscount = CUSTOMER_LEVEL_DISCOUNTS[customer.customer_level] || 0;
                      return (
                      <div
                        key={customer.id}
                        onClick={async () => {
                          if (customer.is_blocked && transactionType === "ACCOUNT SALE") {
                            toast.error(`Account ${customer.account_number || ''} is blocked.`);
                            return;
                          }
                          // Fetch fresh customer data to get live balance for all account-related transactions
                          if (isOnline) {
                            try {
                              const response = await axios.get(`${API}/customers/${customer.id}`);
                              if (response.data) {
                                setSelectedCustomer(response.data);
                                setCustomerOrderNumber("");
                                return;
                              }
                            } catch (error) {
                              console.error("Error fetching fresh customer data:", error);
                              // Fall back to cached data if fetch fails
                            }
                          }
                          setSelectedCustomer(customer);
                          setCustomerOrderNumber(""); // Clear order number when changing customer
                        }}
                        className={`p-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0 ${
                          selectedCustomer?.id === customer.id ? (
                            transactionType === "ACCOUNT REFUND" ? "bg-orange-100" : 
                            transactionType === "CUSTOMER SALE" ? "bg-blue-100" : "bg-purple-100"
                          ) : ""
                        } ${customer.is_blocked && transactionType === "ACCOUNT SALE" ? "bg-red-50 opacity-70" : ""}`}
                        data-testid={`customer-option-${customer.id}`}
                      >
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-sm">{customer.name}</span>
                          {customer.account_number && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">
                              {customer.account_number}
                            </span>
                          )}
                          {customer.customer_level && customer.customer_level !== "Standard" && (
                            <span className={`text-xs px-1 rounded flex items-center gap-0.5 ${
                              customer.customer_level === "Level 1" ? "bg-yellow-100 text-yellow-700" : "bg-purple-100 text-purple-700"
                            }`}>
                              <Star className="h-2 w-2" />
                              {levelDiscount}%
                            </span>
                          )}
                          {customer.requires_order_number && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-1 rounded">ORDER#</span>
                          )}
                          {customer.is_blocked && (
                            <span className="text-xs bg-red-100 text-red-700 px-1 rounded">BLOCKED</span>
                          )}
                          {customer.loyalty_credit_balance > 0 && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-1 rounded flex items-center gap-0.5">
                              <Gift className="h-2 w-2" />
                              R{customer.loyalty_credit_balance.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    {filteredCustomers.length === 0 && (
                      <div className="p-2 text-center text-gray-500 text-xs">No customers found</div>
                    )}
                  </div>
                </div>
              )}
              
              {transactionType === "CASH REFUND" && (
                <div className="border-2 border-orange-300 rounded-lg p-3 bg-orange-50 h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-sm font-bold text-orange-800 mb-1">Cash Refund</div>
                    <div className="text-2xl font-bold text-orange-700">
                      Give: R {roundedTotal.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
              
              {transactionType === "CARD REFUND" && (
                <div className="border-2 border-orange-300 rounded-lg p-3 bg-orange-50 h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-sm font-bold text-orange-800 mb-1">Card Refund</div>
                    <div className="text-2xl font-bold text-orange-700">
                      Refund: R {roundedTotal.toFixed(2)}
                    </div>
                    <div className="text-xs text-orange-600 mt-1">Process refund on card terminal</div>
                  </div>
                </div>
              )}

              {/* Refund reason — required for every refund type. Persisted
                  as `refund_reason` on the sale doc so credit notes and audit
                  reports can show WHY money left the till. */}
              {(transactionType === "CASH REFUND" ||
                transactionType === "CARD REFUND" ||
                transactionType === "ACCOUNT REFUND") && (
                <div className="mt-2 border-2 border-orange-300 rounded-lg p-2 bg-orange-50">
                  <Label className="text-xs font-bold text-orange-800 mb-1 block">
                    Reason for Refund <span className="text-red-600">*</span>
                  </Label>
                  <textarea
                    data-testid="refund-reason-input"
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="e.g. Damaged on delivery, wrong size, customer changed mind…"
                    rows={2}
                    className="w-full text-xs p-2 border border-orange-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
                    maxLength={200}
                  />
                  <div className={`text-[10px] mt-1 flex justify-between ${refundReason.trim().length < 3 ? "text-red-600" : "text-orange-700"}`}>
                    <span>
                      {refundReason.trim().length < 3
                        ? "Required — at least 3 characters"
                        : "Reason will be printed on the credit note and saved to audit."}
                    </span>
                    <span>{refundReason.length}/200</span>
                  </div>
                </div>
              )}
              
              {transactionType === "CASH SALE" && (
                <div className="text-sm text-gray-500 p-3">
                  Select payment method below
                </div>
              )}
              
              {transactionType === "CUSTOMER SALE" && !selectedCustomer && (
                <div className="text-sm text-blue-600 p-3">
                  Select a customer, then choose payment method
                </div>
              )}
              
              {transactionType === "CUSTOMER SALE" && selectedCustomer && (
                <div className="text-sm text-blue-600 p-3">
                  Customer selected - choose payment method below
                </div>
              )}
            </div>
          </div>

          {/* Selected Customer Summary - Compact */}
          {selectedCustomer && (transactionType === "ACCOUNT SALE" || transactionType === "ACCOUNT REFUND" || transactionType === "CUSTOMER SALE") && (
            <div className={`border-2 rounded-lg p-3 ${
              transactionType === "ACCOUNT REFUND" 
                ? 'border-orange-300 bg-orange-50' 
                : transactionType === "CUSTOMER SALE"
                ? 'border-blue-300 bg-blue-50'
                : (hasSufficientCredit() ? 'border-purple-300 bg-purple-50' : 'border-red-300 bg-red-50')
            }`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className={`font-bold ${
                    transactionType === "ACCOUNT REFUND" ? "text-orange-800" : 
                    transactionType === "CUSTOMER SALE" ? "text-blue-800" : "text-purple-800"
                  }`}>
                    {selectedCustomer.name}
                    {selectedCustomer.account_number && <span className="ml-2 text-sm font-normal">({selectedCustomer.account_number})</span>}
                  </div>
                  {transactionType === "CUSTOMER SALE" && (
                    <div className="text-xs text-blue-600 mt-1">Paying with Cash/Card (not on account)</div>
                  )}
                </div>
                {selectedCustomer.customer_level && selectedCustomer.customer_level !== "Standard" && (
                  <span className={`px-2 py-0.5 text-xs font-medium rounded flex items-center gap-1 ${
                    selectedCustomer.customer_level === "Level 1" ? "bg-yellow-100 text-yellow-700" : "bg-purple-200 text-purple-700"
                  }`}>
                    <Star className="h-3 w-3" />
                    {CUSTOMER_LEVEL_DISCOUNTS[selectedCustomer.customer_level]}%
                  </span>
                )}
              </div>

              {/* Opt-in: email this invoice to the customer.
                  Only relevant for sale-document transactions (skip refunds & cash sales without a customer). */}
              {(transactionType === "ACCOUNT SALE" || transactionType === "CUSTOMER SALE") && (
                <label className="flex items-start gap-2 cursor-pointer mt-2 p-2 rounded bg-white border border-blue-200">
                  <input
                    type="checkbox"
                    checked={emailInvoiceToCustomer}
                    onChange={(e) => setEmailInvoiceToCustomer(e.target.checked)}
                    className="mt-0.5 rounded"
                    data-testid="checkout-email-invoice-checkbox"
                  />
                  <span className="text-xs flex-1">
                    <span className="font-medium text-blue-700">📧 Email invoice to customer</span>
                    {selectedCustomer?.email ? (
                      <span className="block text-[11px] text-gray-600 mt-0.5">
                        Will be sent to <span className="font-mono">{selectedCustomer.email}</span>
                      </span>
                    ) : (
                      <span className="block text-[11px] text-amber-700 mt-0.5">
                        No email on file — you&apos;ll be prompted after sale.
                      </span>
                    )}
                  </span>
                </label>
              )}
              
              {transactionType === "ACCOUNT SALE" && !hasSufficientCredit() && (
                <div className="flex items-center gap-2 text-red-700 bg-red-100 p-2 rounded mt-2 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <div>
                    {(selectedCustomer?.credit_limit || 0) <= 0 && !(selectedCustomer?.has_credit_balance) ? (
                      <>
                        <span className="font-medium">No credit facility configured.</span>
                        <div className="text-xs mt-1">Set customer credit limit above R 0.00 to allow account sales, or customer needs a credit balance.</div>
                      </>
                    ) : (
                      <>
                        <span className="font-medium">Insufficient credit!</span>
                        <div className="text-xs mt-1">
                          Available: R {getAvailableCredit().toFixed(2)} | Required: R {roundedTotal.toFixed(2)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show credit info for account sales even when sufficient */}
              {transactionType === "ACCOUNT SALE" && hasSufficientCredit() && (selectedCustomer?.credit_limit > 0 || selectedCustomer?.has_credit_balance) && (
                <div className="text-xs text-purple-600 mt-2">
                  {selectedCustomer?.has_credit_balance ? (
                    <>Credit Balance: R {Math.abs(selectedCustomer.calculated_balance || 0).toFixed(2)} available to use</>
                  ) : (
                    <>Credit: R {getAvailableCredit().toFixed(2)} available of R {selectedCustomer.credit_limit.toFixed(2)} limit</>
                  )}
                </div>
              )}
              
              {transactionType === "ACCOUNT REFUND" && (
                <div className="text-sm text-orange-700 mt-1">
                  R {roundedTotal.toFixed(2)} will be credited back
                </div>
              )}
              
              {/* Loyalty Credit Display - show when customer is selected */}
              {selectedCustomer && transactionType !== "ACCOUNT REFUND" && (
                <div className={`flex items-center gap-2 ${selectedCustomer.loyalty_credit_balance > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-gray-500 bg-gray-50 border-gray-200'} border p-2 rounded mt-2 text-sm`}>
                  <Gift className="h-4 w-4" />
                  <div className="flex-1">
                    <span className="font-medium">Loyalty Credit: R {(selectedCustomer.loyalty_credit_balance || 0).toFixed(2)}</span>
                    {selectedCustomer.loyalty_credit_balance > 0 ? (
                      <div className="text-xs mt-0.5 text-emerald-600">Select LOYALTY as payment method, or use Split Payment</div>
                    ) : (
                      <div className="text-xs mt-0.5">No loyalty credit available</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Order Number Input - shown when customer requires it */}
          {selectedCustomer?.requires_order_number && (
            <div className={`border-2 rounded-lg p-3 ${customerOrderNumber.trim() ? 'border-green-300 bg-green-50' : orderNumberOverrideBy ? 'border-blue-300 bg-blue-50' : 'border-amber-300 bg-amber-50'}`}>
              <Label className="text-sm font-medium mb-2 block text-amber-800">
                Order Number / Reference <span className="text-red-500">*</span>
              </Label>
              <Input
                value={customerOrderNumber}
                onChange={(e) => setCustomerOrderNumber(e.target.value)}
                placeholder="Enter customer's order/PO number"
                className={`${customerOrderNumber.trim() ? 'border-green-400' : 'border-amber-400'}`}
                data-testid="customer-order-number-input"
              />
              {!customerOrderNumber.trim() && !orderNumberOverrideBy && (
                <div className="flex items-center justify-between gap-2 mt-2">
                  <p className="text-xs text-amber-700 flex-1">
                    ⚠️ {selectedCustomer.name} requires an order number before checkout
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => {
                      setOrderNumberPinError("");
                      setOrderNumberPin("");
                      setOrderNumberPinOpen(true);
                    }}
                    data-testid="order-number-override-btn"
                  >
                    <Lock className="h-3 w-3 mr-1" /> Override with PIN
                  </Button>
                </div>
              )}
              {customerOrderNumber.trim() && (
                <p className="text-xs text-green-700 mt-1">
                  ✓ Order number will be printed on the invoice
                </p>
              )}
              {!customerOrderNumber.trim() && orderNumberOverrideBy && (
                <div className="flex items-center justify-between gap-2 mt-2">
                  <p className="text-xs text-blue-700">
                    ✓ Override approved by <strong>{orderNumberOverrideBy}</strong> — sale can proceed without an order number
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] text-blue-700 hover:bg-blue-100"
                    onClick={() => setOrderNumberOverrideBy(null)}
                    data-testid="order-number-override-clear"
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Payment Methods - for CASH SALE and CUSTOMER SALE */}
          {transactionType === "SELF USE" && (
            <div className="border rounded-lg p-3 bg-teal-50 border-teal-200" data-testid="self-use-info">
              <p className="text-xs font-semibold text-teal-800 mb-1">SELF USE - Store Consumption</p>
              <p className="text-xs text-teal-700">No payment required. Stock will be reduced for record-keeping.</p>
              <p className="text-xs text-teal-600 mt-1">{cart.length} item(s) in cart</p>
            </div>
          )}
          {(transactionType === "CASH SALE" || transactionType === "CUSTOMER SALE") && (
          <div>
            <Label className="text-xs font-medium mb-1 block">Payment Method</Label>
            <div className="grid grid-cols-2 gap-2">
              {/* Left side - Payment method buttons */}
              <div className="grid grid-cols-2 gap-1">
                {paymentMethods.map((method) => (
                  <Button
                    key={method.value}
                    type="button"
                    variant={selectedPaymentMethod === method.value ? "default" : "outline"}
                    onClick={() => !method.disabled && handlePaymentMethodClick(method.value)}
                    disabled={method.disabled}
                    className={`h-8 text-xs ${
                      method.disabled
                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        : selectedPaymentMethod === method.value 
                          ? method.value === "LOYALTY_CREDIT" ? "bg-emerald-600 text-white" : "bg-gray-500 text-white" 
                          : method.value === "LOYALTY_CREDIT" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-gray-200 text-gray-700"
                    }`}
                    data-testid={`payment-method-${method.value.toLowerCase()}`}
                  >
                    {method.label}
                    {method.value === "LOYALTY_CREDIT" && selectedCustomer && (
                      <span className="ml-1 text-[10px]">
                        R{(selectedCustomer.loyalty_credit_balance || 0).toFixed(2)}
                      </span>
                    )}
                  </Button>
                ))}
              </div>

              {/* Right side - Amount input */}
              <div>
                {selectedPaymentMethod === "GIFT_VOUCHER" ? (
                  <GiftVoucherTender
                    remaining={roundedTotal - getTotalPaid()}
                    usedCodes={payments.filter((p) => p.reference).map((p) => p.reference)}
                    onAdd={addGiftVoucherPayment}
                  />
                ) : selectedPaymentMethod === "CASH" ? (
                  <>
                    <div className="bg-black text-green-400 text-2xl font-bold text-right p-2 rounded mb-1 font-mono">
                      {paymentAmount || "0.00"}
                    </div>
                    <Input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && paymentAmount && parseFloat(paymentAmount) >= roundedTotal - 0.01 && !processing) {
                          handleCompleteSale();
                        }
                      }}
                      placeholder="Enter amount..."
                      className="text-lg h-10 text-center"
                      step="0.01"
                      data-testid="payment-amount-input"
                      autoFocus
                    />
                  </>
                ) : (
                  <>
                    <div className="bg-black text-green-400 text-3xl font-bold text-right p-3 rounded mb-2 font-mono">
                      {paymentAmount}
                    </div>
                    <Input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && paymentAmount && parseFloat(paymentAmount) >= roundedTotal - 0.01 && !processing) {
                          handleCompleteSale();
                        }
                      }}
                      className="text-lg h-10 text-center"
                      step="0.01"
                      data-testid="payment-amount-input"
                    />
                  </>
                )}
                
                {/* Split Payment Button */}
                {payments.length === 0 && (
                  <Button
                    type="button"
                    onClick={() => setShowSplitPayment(true)}
                    variant="outline"
                    className="mt-3 h-12 w-full border-2 border-purple-400 text-purple-700 hover:bg-purple-50 hover:border-purple-500 font-semibold"
                    data-testid="split-payment-button"
                  >
                    <Split className="h-5 w-5 mr-2" />
                    Split Payment (Cash + Card + Account)
                  </Button>
                )}
                {payments.length > 0 && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowSplitPayment(true)}
                      variant="outline"
                      className="h-8 flex-1 border-purple-400 text-purple-700 hover:bg-purple-50 text-xs"
                      data-testid="edit-split-button"
                    >
                      <Split className="h-4 w-4 mr-1" />
                      Edit Split
                    </Button>
                    <Button
                      type="button"
                      onClick={handleAddPayment}
                      className="h-8 flex-1 bg-blue-600 hover:bg-blue-700 text-xs"
                      data-testid="add-payment-button"
                    >
                      Add Payment
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Change Calculation - Compact */}
            {selectedPaymentMethod === "CASH" && paymentAmount && parseFloat(paymentAmount) > 0 && payments.length === 0 && (
              <div className="mt-3 bg-yellow-100 border-2 border-yellow-400 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">CHANGE:</span>
                  <span className="text-2xl font-bold text-green-700">
                    R {(parseFloat(paymentAmount) - roundedTotal).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Added Payments List - Compact */}
          {(transactionType === "CASH SALE" || hasGiftVoucher) && payments.length > 0 && (
            <div className="border rounded-lg p-3 bg-gray-50">
              <Label className="text-sm font-medium mb-1 block">Payments Added</Label>
              <div className="space-y-1">
                {payments.map((payment, index) => (
                  <div key={index} className="flex justify-between items-center bg-white p-2 rounded border text-sm">
                    <span className="font-semibold">
                      {payment.method === "GIFT_VOUCHER" ? `GIFT VOUCHER ${payment.reference}` : payment.method}
                      {payment.cash_change > 0 && <span className="ml-1 text-xs text-green-700">(+R {payment.cash_change.toFixed(2)} cash change)</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">R {payment.amount.toFixed(2)}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemovePayment(index)}
                        className="text-red-600 h-6 text-xs"
                        data-testid={`remove-payment-${index}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary - Compact */}
          {(transactionType === "CASH SALE" || hasGiftVoucher) && payments.length > 0 && (
          <div className="border-t pt-2 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="font-semibold">Total Paid:</span>
              <span className={`font-bold ${getTotalPaid() >= roundedTotal ? "text-green-600" : "text-red-600"}`}>
                R {getTotalPaid().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="font-semibold">Amount Due:</span>
              <span className="font-bold text-purple-600">R {roundedTotal.toFixed(2)}</span>
            </div>
            
            {getChange() > 0 && (
              <div className="bg-green-100 border-2 border-green-600 rounded-lg p-2 mt-1">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-green-800">CHANGE:</span>
                  <span className="text-2xl font-bold text-green-700" data-testid="change-amount">
                    R {getChange().toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Sale Note */}
        <div className="flex-shrink-0 pt-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={saleNote}
              onChange={(e) => setSaleNote(e.target.value)}
              placeholder="Add a note to this sale (optional)"
              className="flex-1 h-8 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="sale-note-input"
            />
          </div>
        </div>

        {/* Yard-stock notice — cashier hint that 2 receipt copies will print
            because the cart contains items flagged as `is_yard_stock` on
            Stock Master. Prevents the "why did it print twice?" question. */}
        {hasYardStockItem && (
          <div
            className="flex-shrink-0 mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800"
            data-testid="yard-stock-copies-hint"
          >
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">2</span>
            Printing 2 copies — yard stock in cart (customer + yard staff).
          </div>
        )}

        {/* Below-cost override — surfaced after the backend blocks a sale
            for having at least one line whose unit selling price is below
            the recorded cost. The cashier can tick the checkbox to acknowledge
            they're doing a stock-clearance sale, then re-press ACCEPT. The
            flag is persisted on the sale doc as `allow_below_cost=true` for
            audit purposes. State auto-resets when the modal closes. */}
        {belowCostBlockDetail && (
          <div
            className="flex-shrink-0 mt-2 rounded-md border-2 border-rose-400 bg-rose-50 px-3 py-3 text-xs text-rose-900 space-y-2"
            data-testid="below-cost-block-banner"
          >
            <div className="font-bold text-sm">Sale blocked — line(s) below cost</div>
            <div className="whitespace-pre-wrap break-words leading-snug">{belowCostBlockDetail}</div>
            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={allowBelowCost}
                onChange={(e) => setAllowBelowCost(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-rose-600"
                data-testid="allow-below-cost-checkbox"
              />
              <span className="font-semibold">
                Manager approves selling below cost (stock clearance) — re-press ACCEPT to proceed.
                This will be recorded on the sale.
              </span>
            </label>
          </div>
        )}

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex-shrink-0 grid grid-cols-2 gap-3 pt-3 border-t mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={processing}
            className="h-10 text-base border-2"
            data-testid="cancel-button"
          >
            CANCEL
          </Button>
            
            {/* Different button logic for different transaction types */}
            {transactionType === "ACCOUNT SALE" ? (
              <Button
                onClick={handleCompleteSale}
                className={`h-10 text-base ${hasSufficientCredit() && selectedCustomer && !orderNumberBlocked ? "bg-green-600 hover:bg-green-700" : "bg-gray-400"}`}
                disabled={processing || !selectedCustomer || !hasSufficientCredit() || orderNumberBlocked}
                data-testid="complete-sale-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : !selectedCustomer ? (
                  "SELECT CUSTOMER"
                ) : orderNumberBlocked ? (
                  "PO NUMBER REQUIRED"
                ) : !hasSufficientCredit() ? (
                  (selectedCustomer?.credit_limit || 0) <= 0 ? "NO CREDIT FACILITY" : "INSUFFICIENT CREDIT"
                ) : (
                  "ACCEPT"
                )}
              </Button>
            ) : transactionType === "ACCOUNT REFUND" ? (
              <Button
                onClick={handleCompleteSale}
                className={`h-10 text-base ${selectedCustomer && refundReason.trim().length >= 3 ? "bg-orange-600 hover:bg-orange-700" : "bg-gray-400"}`}
                disabled={processing || !selectedCustomer || refundReason.trim().length < 3}
                data-testid="complete-refund-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : !selectedCustomer ? (
                  "SELECT CUSTOMER"
                ) : refundReason.trim().length < 3 ? (
                  "ENTER REASON"
                ) : (
                  "REFUND TO ACCOUNT"
                )}
              </Button>
            ) : transactionType === "CASH REFUND" ? (
              <Button
                onClick={handleCompleteSale}
                className={`h-10 text-base ${refundReason.trim().length >= 3 ? "bg-orange-600 hover:bg-orange-700" : "bg-gray-400"}`}
                disabled={processing || refundReason.trim().length < 3}
                data-testid="complete-refund-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : refundReason.trim().length < 3 ? (
                  "ENTER REASON"
                ) : (
                  "GIVE CASH REFUND"
                )}
              </Button>
            ) : transactionType === "CARD REFUND" ? (
              <Button
                onClick={handleCompleteSale}
                className={`h-10 text-base ${refundReason.trim().length >= 3 ? "bg-orange-600 hover:bg-orange-700" : "bg-gray-400"}`}
                disabled={processing || refundReason.trim().length < 3}
                data-testid="complete-card-refund-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : refundReason.trim().length < 3 ? (
                  "ENTER REASON"
                ) : (
                  "REFUND TO CARD"
                )}
              </Button>
            ) : transactionType === "CUSTOMER SALE" ? (
              <Button
                onClick={handleCompleteSale}
                className={`h-10 text-base ${orderNumberBlocked ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}`}
                disabled={processing || !selectedCustomer || orderNumberBlocked || (!paymentAmount && payments.length === 0) || (payments.length === 0 && (parseFloat(paymentAmount) || 0) < roundedTotal - 0.01)}
                data-testid="complete-customer-sale-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : !selectedCustomer ? (
                  "SELECT CUSTOMER"
                ) : orderNumberBlocked ? (
                  "PO NUMBER REQUIRED"
                ) : (
                  "ACCEPT"
                )}
              </Button>
            ) : transactionType === "SELF USE" ? (
              <Button
                onClick={handleCompleteSale}
                className="h-10 text-base bg-teal-600 hover:bg-teal-700 text-white"
                disabled={processing}
                data-testid="complete-self-use-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "CONFIRM SELF USE"
                )}
              </Button>
            ) : (
              <Button
                onClick={handleCompleteSale}
                className={`h-10 text-base ${orderNumberBlocked ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}`}
                disabled={processing || orderNumberBlocked || (!paymentAmount && payments.length === 0) || (payments.length === 0 && (parseFloat(paymentAmount) || 0) < roundedTotal - 0.01)}
                data-testid="complete-sale-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : orderNumberBlocked ? (
                  "PO NUMBER REQUIRED"
                ) : (
                  "ACCEPT"
                )}
              </Button>
            )}
        </div>
      </DialogContent>

      {/* Receipt Modal - Auto-prints ONLY when the sale needs a paper invoice */}
      {/* Auto-print rule (user-requested Feb 2026):                              */}
      {/*   • ACCOUNT SALE / CUSTOMER SALE / any sale linked to a customer       */}
      {/*     → auto-print (account proof / customer copy required).             */}
      {/*   • Plain walk-in cash/card/EFT counter sales                           */}
      {/*     → modal opens but does NOT auto-print. Cashier can press           */}
      {/*       "Print Invoice" if the customer asks, otherwise close to         */}
      {/*       skip — stops the daily paper waste.                              */}
      {(() => {
        const txType = (completedTransaction?.transaction_type || "").toUpperCase();
        const hasLinkedCustomer = Boolean(completedTransaction?.customer_id);
        const shouldAutoPrint = (
          txType === "ACCOUNT SALE" ||
          txType === "CUSTOMER SALE" ||
          hasLinkedCustomer
        );
        return (
          <SplitInvoiceWrapper
            open={showReceipt}
            onClose={() => {
              setShowReceipt(false);
              const finishedTxn = completedTransaction;
              setCompletedTransaction(null);
              // Reset the idempotency key so the NEXT checkout gets a fresh
              // one. Without this, if the cashier reopens the modal to ring
              // an identical second sale (same cart contents) the backend
              // would happily return the previously-committed sale and no
              // new invoice would be produced.
              idempotencyKeyRef.current = null;
              // Reset all checkout state
              setPayments([]);
              setAllowBelowCost(false);
              setBelowCostBlockDetail("");
              setPaymentAmount("");
              setSelectedCustomer(null);
              setCustomerSearch("");
              setCustomerOrderNumber("");
              setRefundReason("");
              setSaleNote("");
              setTransactionType("CASH SALE");
              setSelectedPaymentMethod("CASH");
              setEmailInvoiceToCustomer(false);
              // Trigger the parent's completion handler to clear cart and reset sales screen
              if (onComplete) onComplete(finishedTxn);
              // Also explicitly close the checkout modal
              if (onClose) onClose();
            }}
            transaction={completedTransaction}
            vatEnabled={vatEnabled}
            autoPrint={shouldAutoPrint}
            copies={receiptCopies}
          />
        );
      })()}

      {/* Email Prompt Dialog - shown when customer has no email */}
      <Dialog open={showEmailPrompt} onOpenChange={setShowEmailPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Send Invoice via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              No email address on file for <strong>{selectedCustomer?.name}</strong>.
              Enter an email address to send the invoice, or cancel to skip.
            </p>
            <div>
              <Label htmlFor="email-prompt">Email Address</Label>
              <Input
                id="email-prompt"
                type="email"
                autoCapitalize={false}
                placeholder="customer@example.com"
                value={emailPromptAddress}
                onChange={(e) => setEmailPromptAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && emailPromptAddress) {
                    handleEmailPromptSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={handleEmailPromptCancel}
              disabled={sendingEmail}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEmailPromptSubmit}
              disabled={!emailPromptAddress || sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Split Payment Modal */}
      <SplitPaymentModal
        open={showSplitPayment}
        onClose={() => setShowSplitPayment(false)}
        total={roundedTotal}
        onConfirm={handleSplitPaymentConfirm}
        hasCustomer={!!selectedCustomer}
        availableCredit={getAvailableCredit()}
        creditLimit={selectedCustomer?.credit_limit || 0}
        loyaltyCredit={selectedCustomer?.loyalty_credit_balance || 0}
      />

      {/* PIN Override — customer requires order number */}
      <Dialog
        open={orderNumberPinOpen}
        onOpenChange={(v) => {
          if (!v) {
            setOrderNumberPinOpen(false);
            setOrderNumberPin("");
            setOrderNumberPinError("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="order-number-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Lock className="h-5 w-5" />
              Manager PIN Override
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1.5">
              <div className="font-semibold text-amber-900">
                {selectedCustomer?.name} requires an order number.
              </div>
              <div className="text-amber-800">
                Enter a manager PIN to allow this sale to complete without one.
                The override will be logged for audit.
              </div>
            </div>
            <form onSubmit={submitOrderNumberOverride} className="space-y-2">
              <Label htmlFor="order-pin" className="text-sm flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" /> Enter PIN
              </Label>
              <Input
                id="order-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                maxLength={6}
                value={orderNumberPin}
                onChange={(e) => {
                  setOrderNumberPin(e.target.value.replace(/\D/g, ""));
                  setOrderNumberPinError("");
                }}
                placeholder="••••"
                className="text-center text-2xl tracking-widest h-12"
                data-testid="order-number-pin-input"
              />
              {orderNumberPinError && (
                <p className="text-sm text-red-600 text-center" data-testid="order-number-pin-error">
                  {orderNumberPinError}
                </p>
              )}
            </form>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOrderNumberPinOpen(false);
                setOrderNumberPin("");
                setOrderNumberPinError("");
              }}
              disabled={orderNumberPinBusy}
              data-testid="order-number-pin-cancel"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              onClick={submitOrderNumberOverride}
              disabled={orderNumberPinBusy || orderNumberPin.length < 4}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="order-number-pin-approve"
            >
              <Lock className="h-4 w-4 mr-1" />
              {orderNumberPinBusy ? "Verifying…" : "Approve Override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default CheckoutModal;
